import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';

interface InputProps {
  label?: string;
  value?: string | undefined;
  onChange?: (value: string | undefined) => void;
  type?: string;
  placeholder?: string;
  prefix?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  ref?: React.Ref<HTMLInputElement> | null;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  errorMessage?: string;
  required?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'before' | 'after';
  actionButton?: React.ReactNode;
  actionButtonPosition?: 'before' | 'after';
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  inputContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    border: '1px solid #ccc',
    borderRadius: '5px',
    transition: 'border-color 0.2s ease',
    overflow: 'hidden',
    '&:hover': {
      border: '1px solid #666666',
    },
    '&:focus': {
      border: '1px solid var(--dai-primary-color)',
      outline: '1px solid var(--dai-primary-color)',
    },
    '&.disabled': {
      cursor: 'not-allowed',
      backgroundColor: '#f5f5f5',
      '&:hover': {
        cursor: 'not-allowed',
        borderColor: '#ccc',
      },
      '& input': {
        cursor: 'not-allowed',
      },
    },
    '&.error': {
      borderColor: '#e53935 !important',
    },
  },
  prefix: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#777777',
    fontSize: '0.8em',
    borderRight: '1px solid #ccc',
    padding: '10px',
    backgroundColor: '#f0f0f0',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },
  input: {
    padding: '10px !important',
    width: '100%',
    border: '0 !important',
    outline: 'none !important',
    background: 'transparent !important',
    fontSize: 'var(--dai-font-size)',
  },
  icon: {
    '&.before': {
      paddingLeft: '8px',
    },
    '&.after': {
      paddingRight: '8px',
    },
    '& svg': {
      display: 'block',
      fill: 'var(--dai-primary-color)',
      width: '20px',
      height: '20px',
    },
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#777777',
    fontSize: '0.8em',
    borderRight: '1px solid #ccc',
    padding: '10px',
    backgroundColor: '#f0f0f0',
  },
  errorText: {
    color: '#e53935',
    fontSize: '12px',
    marginTop: '5px',
  },
});

const InputBox: React.FC<InputProps> = (props) => {
  const classes = useStyles();
  const {
    label,
    value,
    onChange,
    type = 'text',
    placeholder,
    prefix,
    min,
    max,
    maxLength,
    required = false,
    errorMessage,
    className,
    disabled,
    onFocus,
    onBlur,
    actionButton,
    actionButtonPosition = 'after',
    icon,
    iconPosition = 'after',
    ref,
    ...rest
  } = props;

  const [localErrorMessage, setLocalErrorMessage] = useState<
    string | undefined
  >(errorMessage);
  const [valueChanged, setValueChanged] = useState(false);

  useEffect(() => {
    setLocalErrorMessage(errorMessage);
    setValueChanged(false);
  }, [errorMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
    setValueChanged(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (valueChanged && localErrorMessage) {
      setLocalErrorMessage(undefined);
    }
    if (onBlur) onBlur();
  };

  const handleIconClick = () => {
    if (ref && 'current' in ref && ref.current) {
      ref.current.focus();
    }
  };

  return (
    <div className={classes.container} style={{ width: '100%'}}>
      {label && (
        <div className={classes.label}>
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </div>
      )}
      <div
        className={`${classes.inputContainer} ${
          localErrorMessage ? 'error' : ''
        } ${disabled ? 'disabled' : ''}`}
      >
        {prefix && <span className={classes.prefix}>{prefix}</span>}
        <span className={classes.inputWrapper}>
          {actionButton && actionButtonPosition === 'before' && actionButton}
          {icon && iconPosition === 'before' && (
            <span
              className={`${classes.icon} ${iconPosition}`}
              onClick={handleIconClick}
            >
              {icon}
            </span>
          )}
          <input
            className={`${classes.input} ${className || ''}`}
            type={type}
            value={value}
            min={min}
            max={max}
            maxLength={maxLength}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            ref={ref}
            required={required}
            {...rest}
          />
          {icon && iconPosition === 'after' && (
            <span
              className={`${classes.icon} ${iconPosition}`}
              onClick={handleIconClick}
            >
              {icon}
            </span>
          )}
          {actionButton && actionButtonPosition === 'after' && actionButton}
        </span>
      </div>
      {localErrorMessage && (
        <div className={classes.errorText}>{localErrorMessage}</div>
      )}
    </div>
  );
};

export default InputBox;
